export const CONVERSION_RATE_CATEGORY = "Conversion Rate Optimization";

export const AGENT_READINESS_VERSION = "agent-readiness-2026-10";
/** Saved on scans from the earlier engagement catalog. New scans use CONVERSION_AUDIT_VERSION. */
export const ENGAGEMENT_VERSION = "engagement-2026-10";
export const CONVERSION_AUDIT_VERSION = "conversion-audit-2026-10";
/** People Ready 2.0. Historical scans keep conversion-audit / engagement versions. */
export const PEOPLE_READY_VERSION = "people-v2.0";
/** Unified CRO. Historical people-v2.0 / conversion-audit scores are not this formula. */
export const CRO_V5_VERSION = "cro-v5.0";
/** AEO snapshot version. Numeric scores require live labeled answer-engine evidence. */
export const AEO_VERSION = "aeo-v1.0";
export const AEO_PLANNED_VERSION = "aeo-planned-2026-11";

export type CheckConfidence = "high" | "medium" | "low";

export type CheckStatus = "passed" | "failed" | "warning" | "not_applicable" | "not_tested";

/** Display only. Score math still uses status and weight. */
export type Verification = "verified" | "not_detected" | "not_verified" | "not_applicable";

export type Maturity = "established" | "emerging" | "optional";

export type DetectionMethod = "http" | "dns" | "html" | "browser";

export type CheckDefinition = {
  id: string;
  title: string;
  category: string;
  specUrl: string;
  maturity: Maturity;
  detection: DetectionMethod;
  weight: number;
  impact: string;
  fixId?: string;
};

export type CheckResult = CheckDefinition & {
  status: CheckStatus;
  evidence: string;
  verification?: Verification;
  confidence?: CheckConfidence;
  /** Page the evidence came from, when the scan read one. */
  pageUrl?: string;
};

export type CategoryScore = {
  category: string;
  score: number | null;
  passed: number;
  failed: number;
  warnings: number;
  notApplicable: number;
  notTested: number;
};

export type ReportSnapshot = {
  version: string;
  score: number | null;
  coverage: {
    checked: number;
    applicable: number;
    notTested: number;
    notApplicable: number;
  };
  categories: CategoryScore[];
  checks: CheckResult[];
  /** People v2 only. Low-confidence industry used for N/A. */
  industry?: string;
  /** cro-v5.0: share of applicable checks that were conclusive. */
  confidence?: number;
  /** cro-v5.0: how many of the five benchmarks produced a numeric score. */
  benchmarksScored?: number;
  /** cro-v5.0: true only when Conversion Flow was tested past entry. */
  flowTested?: boolean;
};

export type ProbeResult = {
  status: number;
  body: string;
  contentType: string;
  headers: Record<string, string>;
};

/** Fetched pages and DNS text. Evaluation stays pure so tests can supply fixtures. */
export type SiteSnapshot = {
  finalUrl: string;
  homepage: ProbeResult;
  markdown: ProbeResult | null;
  probes: Record<string, ProbeResult | null>;
  /** Flattened TXT records from the apex and `_index._agents` name. */
  dnsTxt: string[];
  /** Homepage plus a small set of contact, location, and booking pages. */
  html: string;
  /**
   * Anonymous read-tool results when a scan discovered an MCP server card.
   * Evaluation stays pure; the scanner fills this before scoring.
   */
  mcpReads?: McpToolRead[];
};

export type McpToolRead = {
  tool: string;
  text: string;
};
